import { ExternalLink, Link as LinkIcon, Loader2, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { trpc } from '../../lib/trpc';
import { Button } from '../../ui/button';
import { EmptyState } from '../empty-state';
import { AddLinkDialog } from './AddLinkDialog';

export interface LinkListProps {
  anchorId: number;
  isReadOnly?: boolean;
  onOpenLink?: (url: string) => void;
}

export function LinkList({ anchorId, isReadOnly = false, onOpenLink }: LinkListProps) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const utils = trpc.useUtils();

  const linksQuery = trpc.links.listByAnchor.useQuery({ anchorId });
  const links = linksQuery.data || [];
  const isLoading = linksQuery.isLoading;

  const createLink = trpc.links.create.useMutation({
    onSuccess: () => {
      utils.links.listByAnchor.invalidate({ anchorId });
      utils.anchors.getPageMetrics.invalidate({ anchorId });
      setDialogOpen(false);
      toast.success('Link saved');
    },
    onError: (err) => {
      toast.error(`Failed to save link: ${err.message}`);
    },
  });

  const deleteLink = trpc.links.delete.useMutation({
    onSuccess: () => {
      utils.links.listByAnchor.invalidate({ anchorId });
      utils.anchors.getPageMetrics.invalidate({ anchorId });
      toast.success('Link deleted');
    },
    onError: (err) => {
      toast.error(`Failed to delete link: ${err.message}`);
    },
  });

  const handleAdd = ({ url, label }: { url: string; label: string }) => {
    createLink.mutate({ anchorId, url, label });
  };

  const openLink = (url: string) => {
    if (onOpenLink) {
      onOpenLink(url);
    } else {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full relative overflow-hidden">
      <div className="flex items-center justify-between px-3 py-2 shrink-0">
        <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Links {links.length > 0 && `(${links.length})`}
        </span>
        {!isReadOnly && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setDialogOpen(true)}
            className="h-7 w-7 p-0"
            aria-label="Save link"
          >
            <Plus className="w-4 h-4" />
          </Button>
        )}
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto">
        {links.length === 0 ? (
          <EmptyState
            icon={LinkIcon}
            title="No links yet"
            description="Save the pages you keep coming back to from here."
            action={
              isReadOnly
                ? undefined
                : {
                    label: 'Save a link',
                    onClick: () => setDialogOpen(true),
                  }
            }
          />
        ) : (
          <ul className="divide-y divide-border">
            {links.map((link) => (
              <li key={link.id} className="flex items-center gap-2 px-3 py-2 group">
                <button
                  type="button"
                  onClick={() => openLink(link.url)}
                  className="flex-1 min-w-0 text-left cursor-pointer"
                >
                  <p className="text-xs font-medium truncate hover:text-primary transition-colors">
                    {link.label}
                  </p>
                  <p className="text-[10px] text-muted-foreground truncate">{link.url}</p>
                </button>
                <ExternalLink className="w-3 h-3 text-muted-foreground shrink-0" />
                {!isReadOnly && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 shrink-0 opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive"
                    onClick={() => deleteLink.mutate({ id: link.id })}
                    aria-label={`Delete ${link.label}`}
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      <AddLinkDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onAdd={handleAdd}
        loading={createLink.isPending}
      />
    </div>
  );
}
